import { Button } from "@/components/ui/button"
import { Heading, Text } from "@/components/ui/typography"
import { sfIcons } from '@/lib/sf-icons'
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { Stepper } from "@supabank/ui"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

type StepsSectionProps = {
  id?: string
}

export function StepsSection({ id }: StepsSectionProps) {
  const { t } = useTranslation("marketing")

  // Steps from SSOT (marketing.json)
  const steps = ((t("stepsSection.steps", { returnObjects: true }) as any[]) || []).map((step: any) => ({
    id: step.id,
    title: step.title,
    description: step.description,
  }))
  return (
    <section
      id={id}
      className={cn(
        "relative py-16 sm:py-24 overflow-hidden",
        "outline-none", // Remove any focus outline (section is not interactive)
        semanticTokens.bg.surface
      )}
      aria-labelledby="landing-steps"
      tabIndex={-1} // Prevent focus outline (section is not interactive)
    >
      {/* Color Lab: Mint Fresh gradient (Cash App style) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse at 30% 70%, hsla(142, 71%, 45%, 0.12) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 30%, hsla(160, 84%, 39%, 0.08) 0%, transparent 50%)
          `
        }}
      />
      <div className="relative z-10 mx-auto max-w-4xl space-y-8 sm:space-y-10 md:space-y-12 px-4 sm:px-6">
        <div className="space-y-3 sm:space-y-4 text-center">
          <Text variant="caption" color="muted" className="uppercase tracking-widest font-medium">
            {t("stepsSection.badge")}
          </Text>
          <Heading level={2} id="landing-steps">
            {t("stepsSection.headline")}{" "}
            {t("stepsSection.headlineHighlight") && (
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                {t("stepsSection.headlineHighlight")}
              </span>
            )}
          </Heading>
          {t("stepsSection.description") && (
            <Text variant="lg" color="muted" className="mx-auto max-w-2xl">
              {t("stepsSection.description")}
            </Text>
          )}
        </div>
        <div className="relative">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-6 top-8 hidden h-[calc(100%-2rem)] md:block"
          >
            <svg viewBox="0 0 400 520" className="h-full w-full">
              <path
                d="M32 0 C 160 120, 240 200, 368 320"
                fill="none"
                className={cn(
                  "stroke-[1.5]",
                  semanticTokens.harmony.calm.border.replace("border", "stroke"),
                  "opacity-40"
                )}
                strokeLinecap="round"
              />
            </svg>
          </div>
          <Stepper steps={steps} animated />
        </div>

        <div className="text-center mt-8 sm:mt-10">
          <Button
            asChild
            variant="outline"
            className={cn(
              "gap-2",
              "min-h-[44px] touch-manipulation",
              semanticTokens.focus.ring
            )}
          >
            <Link to={t("stepsSection.cta.href")}>
              {t("stepsSection.cta.label")}
              <sfIcons.next className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  )
}
